
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SchemaFormRendererProps, SchemaFormField, SchemaFormUIConfig } from '../types';
import { evaluateFieldConditions } from '../utils/conditionEvaluator';
import * as DefaultComponents from './components/DefaultComponents';

// =============================================================================
// Validation utilities
// =============================================================================

interface FieldError {
  message: string;
}

const validateField = (
  fieldId: string,
  value: any,
  field: SchemaFormField,
  isRequired: boolean
): FieldError | null => {
  // Required check
  if (isRequired) {
    if (value === undefined || value === null || value === '') return { message: 'This field is required' };
    if (Array.isArray(value) && value.length === 0) return { message: 'This field is required' };
  }

  // Skip further validation if empty and not required
  if (value === undefined || value === null || value === '') return null;

  if (field.type === 'string' || field.type === 'array') {
    const strVal = typeof value === 'string' ? value : '';
    if (field.minLength !== undefined && strVal.length < field.minLength) {
      return { message: `Must be at least ${field.minLength} characters` };
    }
    if (field.maxLength !== undefined && strVal.length > field.maxLength) {
      return { message: `Must be at most ${field.maxLength} characters` };
    }
    if (field.pattern) {
      try {
        if (!new RegExp(field.pattern).test(strVal)) {
          return { message: 'Invalid format' };
        }
      } catch { /* invalid regex, skip */ }
    }
  }

  if (field.type === 'number' || field.type === 'integer') {
    const numVal = Number(value);
    if (isNaN(numVal)) return { message: 'Must be a valid number' };
    if (field.minimum !== undefined && numVal < field.minimum) {
      return { message: `Must be at least ${field.minimum}` };
    }
    if (field.maximum !== undefined && numVal > field.maximum) {
      return { message: `Must be at most ${field.maximum}` };
    }
  }

  if (field.type === 'table' && Array.isArray(value)) {
    if (field.minRows !== undefined && value.length < field.minRows) {
      return { message: `At least ${field.minRows} row(s) required` };
    }
    if (field.maxRows !== undefined && value.length > field.maxRows) {
      return { message: `At most ${field.maxRows} row(s) allowed` };
    }
  }

  return null;
};

// =============================================================================
// Component
// =============================================================================

export const SchemaFormRenderer: React.FC<SchemaFormRendererProps> = ({ 
  config, 
  onSubmit, 
  isSubmitting = false,
  className = '',
  initialData = {},
  components = {}
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, FieldError>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const schemaPropertiesRef = useRef<string>('');

  const {
    Button = DefaultComponents.Button,
    Input = DefaultComponents.Input,
    Textarea = DefaultComponents.Textarea,
    Label = DefaultComponents.Label,
    Select = DefaultComponents.Select,
    Checkbox = DefaultComponents.Checkbox,
    RadioGroup = DefaultComponents.RadioGroup,
    Switch = DefaultComponents.Switch,
    TableInput = DefaultComponents.TableInput,
  } = components;

  const schemaPropertiesKey = useMemo(() => 
    JSON.stringify(config.schema.properties), [config.schema.properties]);
  
  const initialDataKey = useMemo(() => 
    JSON.stringify(initialData), [initialData]);

  useEffect(() => {
    if (schemaPropertiesRef.current === schemaPropertiesKey + initialDataKey && isInitialized) {
      return;
    }

    const combinedData: Record<string, any> = {};
    
    Object.entries(config.schema.properties).forEach(([fieldId, field]) => {
      if (field.default !== undefined && (!isInitialized || !touchedFields.has(fieldId))) {
        combinedData[fieldId] = field.default;
      }
    });
    
    Object.entries(initialData).forEach(([fieldId, value]) => {
      if (config.schema.properties[fieldId] && value !== undefined && (!isInitialized || !touchedFields.has(fieldId))) {
        combinedData[fieldId] = value;
      }
    });
    
    if (!isInitialized || Object.keys(combinedData).length > 0) {
      setFormData(prev => ({ ...prev, ...combinedData }));
      schemaPropertiesRef.current = schemaPropertiesKey + initialDataKey;
      setIsInitialized(true);
    }
  }, [schemaPropertiesKey, initialDataKey, config.schema.properties, initialData, touchedFields, isInitialized]);

  const validateAll = useCallback((): Record<string, FieldError> => {
    const newErrors: Record<string, FieldError> = {};
    const orderedFieldIds = config.fieldOrder || Object.keys(config.schema.properties);
    
    orderedFieldIds.forEach(fieldId => {
      const field = config.schema.properties[fieldId];
      if (!field) return;
      if (!evaluateFieldConditions(field.conditions, field.showWhen, formData)) return;
      
      const isRequired = config.schema.required?.includes(fieldId) || false;
      const error = validateField(fieldId, formData[fieldId], field, isRequired);
      if (error) newErrors[fieldId] = error;
    });
    
    return newErrors;
  }, [config, formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    
    const validationErrors = validateAll();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) return;
    
    const visibleData: Record<string, any> = {};
    Object.entries(config.schema.properties).forEach(([fieldId, field]) => {
      if (evaluateFieldConditions(field.conditions, field.showWhen, formData)) {
        visibleData[fieldId] = formData[fieldId];
      }
    });
    
    onSubmit(visibleData);
  };

  const updateFormData = useCallback((fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    setTouchedFields(prev => new Set(prev).add(fieldId));
    
    // Clear error on change if submitted
    if (submitted) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  }, [submitted]);

  const renderField = useCallback((fieldId: string, field: SchemaFormField) => {
    if (!evaluateFieldConditions(field.conditions, field.showWhen, formData)) {
      return null;
    }

    const value = formData[fieldId] ?? '';
    const uiConfig = config.uiSchema[fieldId] as SchemaFormUIConfig || {};
    const isRequired = config.schema.required?.includes(fieldId) || false;
    const isDisabled = uiConfig['ui:disabled'] === true;
    const fieldError = errors[fieldId];

    const renderInput = () => {
      switch (field.type) {
        case 'table':
          const tableValue = Array.isArray(value) ? value : [];
          return (
            <TableInput
              columns={field.columns || []}
              value={tableValue}
              onChange={(val: Record<string, any>[]) => updateFormData(fieldId, val)}
              minRows={field.minRows}
              maxRows={field.maxRows}
            />
          );

        case 'boolean':
          if (field.format === 'switch') {
            return (
              <div className="flex items-center space-x-2">
                <Switch
                  id={fieldId}
                  checked={value === true}
                  onCheckedChange={(checked) => updateFormData(fieldId, checked)}
                  disabled={isDisabled}
                />
                <Label htmlFor={fieldId}>{field.title}</Label>
              </div>
            );
          } else {
            return (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={fieldId}
                  checked={value === true}
                  onCheckedChange={(checked) => updateFormData(fieldId, checked)}
                  disabled={isDisabled}
                />
                <Label htmlFor={fieldId}>{field.title}</Label>
              </div>
            );
          }

        case 'string':
          switch (field.format) {
            case 'textarea':
              return (
                <Textarea
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateFormData(fieldId, e.target.value)}
                  placeholder={uiConfig['ui:placeholder']}
                  rows={uiConfig['ui:options']?.rows || 4}
                  disabled={isDisabled}
                />
              );

            case 'select':
              return (
                <Select
                  value={value}
                  onValueChange={(val) => updateFormData(fieldId, val)}
                  placeholder={uiConfig['ui:placeholder'] || `Select ${field.title?.toLowerCase()}`}
                  disabled={isDisabled}
                >
                  {(field.enum || []).map((option, index) => (
                    <option key={index} value={String(option)}>
                      {field.enumNames?.[index] || String(option)}
                    </option>
                  ))}
                </Select>
              );

            case 'combobox':
              // Combobox rendered as a searchable select using default components
              return (
                <Select
                  value={value}
                  onValueChange={(val) => updateFormData(fieldId, val)}
                  placeholder={uiConfig['ui:placeholder'] || `Search ${field.title?.toLowerCase()}`}
                  disabled={isDisabled}
                >
                  {(field.enum || []).map((option, index) => (
                    <option key={index} value={String(option)}>
                      {field.enumNames?.[index] || String(option)}
                    </option>
                  ))}
                </Select>
              );

            case 'radio':
              return (
                <RadioGroup
                  value={value}
                  onValueChange={(val) => updateFormData(fieldId, val)}
                >
                  {(field.enum || []).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <DefaultComponents.RadioGroupItem 
                        value={String(option)} 
                        id={`${fieldId}-${index}`}
                        disabled={isDisabled}
                      />
                      <Label htmlFor={`${fieldId}-${index}`}>
                        {field.enumNames?.[index] || String(option)}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              );

            case 'checkboxes':
            case 'multiselect':
              const selectedValues = Array.isArray(value) ? value : [];
              return (
                <div className="space-y-2">
                  {(field.enum || []).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${fieldId}-${index}`}
                        checked={selectedValues.includes(String(option))}
                        onCheckedChange={(checked) => {
                          const newValues = checked
                            ? [...selectedValues, String(option)]
                            : selectedValues.filter(v => v !== String(option));
                          updateFormData(fieldId, newValues);
                        }}
                        disabled={isDisabled}
                      />
                      <Label htmlFor={`${fieldId}-${index}`}>
                        {field.enumNames?.[index] || String(option)}
                      </Label>
                    </div>
                  ))}
                </div>
              );

            case 'file':
              return (
                <Input
                  type="file"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];
                    updateFormData(fieldId, file);
                  }}
                  accept={uiConfig['ui:options']?.accept}
                  disabled={isDisabled}
                />
              );

            default:
              return (
                <Input
                  type={field.format === 'email' ? 'email' : field.format === 'date' ? 'date' : field.format === 'date-time' ? 'datetime-local' : 'text'}
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData(fieldId, e.target.value)}
                  placeholder={uiConfig['ui:placeholder']}
                  disabled={isDisabled}
                />
              );
          }

        case 'number':
        case 'integer':
          return (
            <Input
              type="number"
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData(fieldId, field.type === 'integer' ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0)}
              placeholder={uiConfig['ui:placeholder']}
              min={field.minimum}
              max={field.maximum}
              step={field.type === 'integer' ? 1 : 'any'}
              disabled={isDisabled}
            />
          );

        default:
          return (
            <Input
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData(fieldId, e.target.value)}
              placeholder={uiConfig['ui:placeholder']}
              disabled={isDisabled}
            />
          );
      }
    };

    return (
      <div key={fieldId} className="space-y-2">
        {field.type !== 'boolean' && (
          <Label htmlFor={fieldId}>
            {field.title || fieldId}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </Label>
        )}
        {renderInput()}
        {fieldError && (
          <p className="text-sm text-red-500">{fieldError.message}</p>
        )}
        {field.description && (
          <p className="text-sm text-gray-600">{field.description}</p>
        )}
        {uiConfig['ui:help'] && (
          <p className="text-sm text-gray-600">{uiConfig['ui:help']}</p>
        )}
      </div>
    );
  }, [formData, config.uiSchema, config.schema.required, updateFormData, errors]);

  const orderedFieldIds = config.fieldOrder || Object.keys(config.schema.properties);
  const filteredFieldIds = orderedFieldIds.filter(fieldId => config.schema.properties[fieldId]);

  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h2 className="text-2xl font-bold">{config.title}</h2>
        {config.description && (
          <p className="text-gray-600 mt-2">{config.description}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {filteredFieldIds.map((fieldId) =>
          renderField(fieldId, config.schema.properties[fieldId])
        )}

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : config.settings.submitButtonText}
        </Button>
      </form>
    </div>
  );
};
